import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, Input, Button, Divider, Collapse, Segmented, Select } from 'antd';
import styled from 'styled-components';
import { PlusOutlined, CloseOutlined, InfoCircleOutlined, PhoneOutlined } from '@ant-design/icons';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import SortIcon from '../../assets/images/SortIcon.png';
import { ErrorIcon, WarningIcon } from '../../assets/icons';
import { validateRecipients } from '../../utils/ESignModalValidation';
import Loader from "../Loader/Loader";
import { MessageModal } from '../common/Modals';
import { useSelector } from 'react-redux';
import { fetchSuggestedESignRecipientsApi } from '../../service/matter';
import PlaceSignatureBoxes from './PlaceSignatureBoxes';
import { alignBoxesToRecipients, orphanedBoxes, signerColor } from './esignBoxes';
import {
  applyStoredSignatureChoice,
  buildRoleSelectOptions,
  canUseStoredSignature,
  computeEffectiveMissingRoles,
  ESIGN_ROLE_LABELS,
  FALLBACK_REQUIRED_SIGNER_ROLES,
  missingRoleLabel,
  normalizeSignerRolesForSubmit,
  STORED_SIGNATURE,
} from '../../utils/eSignRoleUtils';

const ModalHeader = styled.div`
  width: 100%;
  height: 62px;
  padding: 28px 20px 20px 20px;
  background: #FFFFFF;
`;

const ModalTitle = styled.h2`
  font-family: Manrope, sans-serif;
  font-weight: 600;
  font-size: 20px;
  line-height: 30px;
  color: #1B2028;
  margin: 0;
`;

const ModalContent = styled.div`
  width: 100%;
  padding: 0 24px 0 24px;
  background: #fff;
`;

const FileSection = styled.div`
  margin-top: 24px;
  margin-bottom: 16px;
`;

const FileLabel = styled.p`
  font-family: Manrope, sans-serif;
  font-weight: 500;
  font-size: 13px;
  line-height: 100%;
  color: #1B2028;
  margin: 0 0 8px 0;
`;

const FileInput = styled(Input)`
  width: 100%;
  height: 40px;
  border-radius: 6px;
  background: #F3F4F7;
  color: #5A6C87;
  font-family: Manrope, sans-serif;
  font-size: 13px;
  font-weight: 500;
  border: none;
  margin-bottom: 0;
  &.ant-input[disabled] {
    color: #5A6C87;
    background: #F3F4F7;
  }
`;

const RecipientsSection = styled.div`
  margin-bottom: 16px;
`;

const RecipientsLabel = styled.p`
  font-family: Manrope, sans-serif;
  font-weight: 500;
  font-size: 13px;
  line-height: 100%;
  color: #1B2028;
  margin: 0 0 8px 0;
`;

const RecipientBox = styled.div`
  width: 100%;
  background: #F3F4F7;
  border: 1px solid #DDE2E9;
  border-radius: 6px;
  padding: 12px 16px;
  margin-bottom: 12px;
  display: flex;
  align-items: flex-start;
  gap: 8px;
`;

const DragHandle = styled.span`
  cursor: grab;
  color: #B0B7C3;
  font-size: 18px;
  margin-left: 8px;
  display: flex;
  align-items: center;
  height: 40px;
`;

const RecipientNumber = styled.span`
  display: flex;
  height: 40px;
  width: 16px;
  align-items: center;
  font-family: Manrope, sans-serif;
  font-size: 14px;
  color: #1b2028;
  font-weight: 500;
  flex-shrink: 0;
`;

const NameInput = styled(Input)`
  width: 100%;
  min-width: 120px;
  height: 40px;
  border-radius: 6px;
  border: 1px solid #DDE2E9;
  background: #FFFFFF;
  font-family: Manrope, sans-serif;
  font-size: 13px;
  &::placeholder {
    color: #B0B7C3;
    font-weight: 500;
  }
`;

const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 33%;
`;

const EmailInput = styled(Input)`
  width: 100%;
  min-width: 160px;
  height: 40px;
  border-radius: 6px;
  border: 1px solid #DDE2E9;
  background: #FFFFFF;
  font-family: Manrope, sans-serif;
  font-size: 13px;
  &::placeholder {
    color: #B0B7C3;
    font-weight: 500;
  }
`;

const RoleSelect = styled(Select)`
  width: 100%;
  min-width: 60px;
  font-family: Manrope, sans-serif;
  font-size: 13px;
  .ant-select-selector {
    height: 40px !important;
    border-radius: 6px !important;
    border: 1px solid #DDE2E9 !important;
    align-items: center;
  }
`;

const PhoneInput = styled(Input)`
  width: 100%;
  height: 40px;
  border-radius: 6px;
  border: 1px solid #DDE2E9;
  background: #FFFFFF;
  font-family: Manrope, sans-serif;
  font-size: 13px;
  &::placeholder {
    color: #B0B7C3;
    font-weight: 500;
  }
`;

const DeliveryRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  margin-top: 6px;
`;

const StoredSignatureHint = styled.span`
  font-family: Manrope, sans-serif;
  font-size: 12px;
  color: #5A6C87;
`;

const RemoveBtn = styled(Button)`
  min-width: 32px;
  height: 40px;
  border: none;
  background: transparent;
  color: #7688A3;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const AddRecipientLink = styled.a`
  color: #0C53BB;
  font-family: Manrope, sans-serif;
  font-weight: 600;
  font-size: 13px;
  margin-left: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const StyledDivider = styled(Divider)`
  margin: 24px 0 16px 0 !important;
  border-block-start: 1px solid #DDE2E9 !important;
`;

const NoteSection = styled.div`
  margin-bottom: 0;
`;

const NoteLabel = styled.p`
  font-family: Manrope, sans-serif;
  font-weight: 500;
  font-size: 13px;
  line-height: 100%;
  color: #1B2028;
  margin: 0 0 8px 0;
`;

const NoteInput = styled(Input.TextArea)`
  width: 100%;
  border-radius: 6px;
  border: 1px solid #DDE2E9;
  background: #FFFFFF;
  font-family: Manrope, sans-serif;
  font-size: 13px;
  &::placeholder {
    color: #B0B7C3;
    font-weight: 500;
  }
`;

const TagInfoBox = styled.div`
  margin-bottom: 16px;
  .ant-collapse {
    border: 1px solid #d6e4f0;
    border-radius: 6px;
    background: #f0f6ff;
  }
  .ant-collapse-header {
    padding: 8px 12px !important;
    font-family: Manrope, sans-serif;
    font-size: 13px;
    font-weight: 600;
    color: #0C53BB !important;
  }
  .ant-collapse-content-box {
    padding: 0 12px 12px 12px !important;
    font-family: Manrope, sans-serif;
    font-size: 12px;
    color: #3d4f66;
    line-height: 1.7;
  }
`;

const TagCode = styled.code`
  background: #e2ecf7;
  padding: 1px 5px;
  border-radius: 3px;
  font-size: 12px;
  font-family: 'SF Mono', 'Menlo', monospace;
  color: #1a3a5c;
`;

const TagTable = styled.table`
  width: 100%;
  margin-top: 6px;
  border-collapse: collapse;
  td, th {
    padding: 3px 8px;
    text-align: left;
    font-size: 12px;
    border-bottom: 1px solid #d6e4f0;
  }
  th {
    font-weight: 600;
    color: #1B2028;
  }
`;

const Footer = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 24px 0 0 0;
  background: #fff;
`;

const CancelBtn = styled(Button)`
  background: #fff;
  color: #1B2028;
  border: 1px solid #DDE2E9;
  border-radius: 6px;
  font-family: Manrope, sans-serif;
  font-weight: 500;
  font-size: 14px;
  height: 35px;
`;

const SendBtn = styled(Button)`
  background: #0C53BB;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-family: Manrope, sans-serif;
  font-weight: 500;
  font-size: 14px;
  height: 35px;
  opacity: ${props => props.disabled ? 0.8 : 1};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
`;

const ErrorText = styled.div`
  display: flex;
  align-items: center;
  color: #C63939;
  gap: 4px;
  font-size: 12px;
  margin-top: 4px;
`;

// Read-only recipient list shown once boxes have been placed: the people
// were chosen on the placement screen, so here they are verified, not typed.
const ReadoutList = styled.ol`
  list-style: none;
  margin: 0 0 8px 0;
  padding: 0;
  border: 1px solid #DDE2E9;
  border-radius: 6px;
  background: #F6F8FB;
`;

const ReadoutRow = styled.li`
  display: flex;
  align-items: baseline;
  gap: 10px;
  padding: 8px 12px;
  font-family: Manrope, sans-serif;
  font-size: 13px;
  color: #1B2028;
  border-bottom: 1px solid #E5EAF1;
  &:last-child { border-bottom: none; }
`;

const ReadoutNumber = styled.span`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  color: #fff;
  background: ${({ $color }) => $color};
  flex-shrink: 0;
  align-self: center;
`;

const ReadoutName = styled.span`
  font-weight: 600;
  min-width: 140px;
`;

const ReadoutMeta = styled.span`
  color: #5B6B82;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ReadoutBoxes = styled.span`
  font-size: 12px;
  color: ${({ $warn }) => ($warn ? '#C63939' : '#5B6B82')};
  white-space: nowrap;
`;

const ReadoutActions = styled.div`
  display: flex;
  gap: 14px;
  font-size: 13px;
  margin-bottom: 4px;
  a { color: #0C53BB; cursor: pointer; font-weight: 500; }
`;

const RecipientNotice = styled.div`
  border: 1px solid ${props => props.$error ? '#F4B3B3' : '#BBD4F8'};
  background: ${props => props.$error ? '#FFF5F5' : '#F4F8FF'};
  color: ${props => props.$error ? '#9B1C1C' : '#24476F'};
  border-radius: 6px;
  padding: 10px 12px;
  margin: 0 0 12px 0;
  font-family: Manrope, sans-serif;
  font-size: 13px;
  line-height: 1.5;
`;

const errorInputStyle = {
  border: '1px solid #C63939',
};

const KNOWN_SIGNERS = [
  { name: 'Adam Werner', email: 'AWerner@wernerhoffman.com' },
  { name: 'Brendan Garcia', email: 'BGarcia@wernerhoffman.com' },
  { name: 'Matthew Greig', email: 'MGreig@wernerhoffman.com' },
  { name: 'Michael Hoffman', email: 'MHoffman@wernerhoffman.com' },
];

// Sortable recipient row component
function SortableRecipient({
  id,
  idx,
  recipient,
  onChange,
  onRemove,
  canRemove,
  listeners,
  attributes,
  isDragging,
  errors,
  isFirst,
  roleOptions,
  storedSignatureAvailable,
}) {
  const usesStoredSignature = storedSignatureAvailable && recipient.signature_method === STORED_SIGNATURE;
  const showPhone = !usesStoredSignature
    && (recipient.delivery_method === 'SMS' || recipient.delivery_method === 'BOTH');
  const signerOptionsId = `known-signers-${id}`;

  return (
    <RecipientBox style={{ opacity: isDragging ? 0.7 : 1, flexWrap: 'wrap' }}>
      <RecipientNumber>{idx + 1}.</RecipientNumber>
      <InputWrapper>
        <NameInput
          autoFocus={isFirst}
          list={signerOptionsId}
          placeholder="Name"
          value={recipient.name}
          onChange={e => onChange(idx, 'name', e.target.value)}
          style={errors?.name ? errorInputStyle : {}}
        />
        <datalist id={signerOptionsId}>
          {KNOWN_SIGNERS.map(({ name }) => (
            <option key={name} value={name} />
          ))}
        </datalist>
        {errors?.name && <ErrorText><ErrorIcon /> {errors.name}</ErrorText>}
      </InputWrapper>
      <InputWrapper>
        <EmailInput
          placeholder="Enter email"
          value={recipient.email}
          onChange={e => onChange(idx, 'email', e.target.value)}
          style={errors?.email ? errorInputStyle : {}}
        />
        {errors?.email && <ErrorText><ErrorIcon /> {errors.email}</ErrorText>}
      </InputWrapper>
      <InputWrapper>
        <RoleSelect
          data-testid="recipient-role-select"
          placeholder="Role"
          value={recipient.role || undefined}
          options={roleOptions}
          onChange={(value) => onChange(idx, 'role', value)}
          style={errors?.role ? errorInputStyle : {}}
        />
        {errors?.role && <ErrorText><ErrorIcon /> {errors.role}</ErrorText>}
      </InputWrapper>
      {canRemove && (
        <>
          <DragHandle {...listeners} {...attributes} tabIndex={-1} role={undefined}>
            <img src={SortIcon} alt="Sort" style={{ width: 20, height: 20, opacity: 0.7 }} />
          </DragHandle>
          <RemoveBtn tabIndex={-1} data-testid="remove-recipient" icon={<CloseOutlined />} onClick={() => onRemove(idx)} />
        </>
      )}
      <DeliveryRow>
        <span style={{ width: 16, flexShrink: 0 }} />
        {storedSignatureAvailable && (
          <Segmented
            size="small"
            data-testid="attorney-signature-method"
            value={usesStoredSignature ? STORED_SIGNATURE : 'email'}
            onChange={val => onChange(idx, 'signature_method', val)}
            options={[
              { label: 'Email them to sign', value: 'email' },
              { label: 'Use stored signature', value: STORED_SIGNATURE },
            ]}
          />
        )}
        {usesStoredSignature ? (
          <StoredSignatureHint data-testid="stored-signature-hint">
            Their signature, name and date are added after everyone else signs.
          </StoredSignatureHint>
        ) : (
          <Segmented
            size="small"
            value={recipient.delivery_method || 'EMAIL'}
            onChange={val => onChange(idx, 'delivery_method', val)}
            options={[
              { label: 'Email', value: 'EMAIL' },
              { label: 'SMS', value: 'SMS' },
              { label: 'Both', value: 'BOTH' },
            ]}
          />
        )}
        {showPhone && (
          <InputWrapper style={{ width: 'auto', flex: 1 }}>
            <PhoneInput
              placeholder="Phone number"
              prefix={<PhoneOutlined style={{ color: '#B0B7C3' }} />}
              value={recipient.phone}
              onChange={e => onChange(idx, 'phone', e.target.value)}
              style={errors?.phone ? errorInputStyle : {}}
            />
            {errors?.phone && <ErrorText><ErrorIcon /> {errors.phone}</ErrorText>}
          </InputWrapper>
        )}
      </DeliveryRow>
    </RecipientBox>
  );
}

SortableRecipient.propTypes = {
  id: PropTypes.string.isRequired,
  idx: PropTypes.number.isRequired,
  isFirst: PropTypes.bool,
  recipient: PropTypes.shape({
    name: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    role: PropTypes.string.isRequired,
    phone: PropTypes.string,
    delivery_method: PropTypes.string,
    signature_method: PropTypes.string,
  }).isRequired,
  storedSignatureAvailable: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  canRemove: PropTypes.bool.isRequired,
  listeners: PropTypes.object.isRequired,
  attributes: PropTypes.object.isRequired,
  isDragging: PropTypes.bool.isRequired,
  roleOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
};

const SortableRecipientWrapper = (props) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: props.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 2 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      tabIndex={-1}
      data-testid="recipient-row"
      data-recipient-id={props.id}
    >
      <SortableRecipient {...props} listeners={listeners} attributes={attributes} isDragging={isDragging} />
    </div>
  );
};

SortableRecipientWrapper.propTypes = {
  id: PropTypes.string.isRequired,
  ...SortableRecipient.propTypes,
};

const emptyRecipient = () => ({
  id: `recipient-${Date.now()}-${Math.random().toString(36).slice(2)}`,
  name: '',
  email: '',
  role: '',
  phone: '',
  delivery_method: 'EMAIL',
});

const recipientFromSuggestedSigner = (signer, index) => ({
  id: `suggested-${signer.role || index}-${index}`,
  name: signer.name || ESIGN_ROLE_LABELS[signer.role] || '',
  email: signer.email || '',
  role: signer.role || '',
  phone: signer.phone || '',
  delivery_method: signer.delivery_method || 'EMAIL',
  order: signer.order || index + 1,
});

const ESignModal = ({
  openESignModal,
  setOpenESignModal,
  selectedDoc,
  handleSubmitESign,
  handleSnackbar,
  startWithBoxPlacement,
}) => {
  const [recipients, setRecipients] = useState([emptyRecipient()]);
  const [isLoading, setisLoading] = useState(false);
  const [suggestedRecipientsLoading, setSuggestedRecipientsLoading] = useState(false);
  const [initialMissingRoles, setInitialMissingRoles] = useState([]);
  const [suggestedRecipientsError, setSuggestedRecipientsError] = useState('');
  const [nowhereToSignMessage, setNowhereToSignMessage] = useState('');
  const [storedSignatureNames, setStoredSignatureNames] = useState([]);
  const [placedBoxes, setPlacedBoxes] = useState([]);
  const [placingBoxes, setPlacingBoxes] = useState(false);
  // Once boxes are placed the recipient list is a readout; this flips it back
  // to the full editor for the odd correction (typo, stored signature).
  const [editingRecipientDetails, setEditingRecipientDetails] = useState(false);
  const autoOpenedPlacementRef = React.useRef(false);

  const [errors, setErrors] = useState({});
  
  const [notes, setNotes] = useState('');

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    })
  );

  const isESignEnabled = useSelector((state) => state.featureFlags.isESignEnabled);

  const roleOptions = useMemo(
    () => buildRoleSelectOptions(FALLBACK_REQUIRED_SIGNER_ROLES),
    []
  );

  const effectiveMissingRoles = useMemo(
    () => computeEffectiveMissingRoles(initialMissingRoles, recipients),
    [initialMissingRoles, recipients]
  );

  useEffect(() => {
    if (!openESignModal || !selectedDoc?.id) return;

    let cancelled = false;
    const loadSuggestedRecipients = async () => {
      setSuggestedRecipientsLoading(true);
      setSuggestedRecipientsError('');
      setInitialMissingRoles([]);
      setNowhereToSignMessage('');
      setPlacedBoxes([]);
      try {
        const res = await fetchSuggestedESignRecipientsApi(selectedDoc.id);
        if (cancelled) return;
        const signers = res?.data?.signers || [];
        const missingRoles = res?.data?.missing_roles || [];
        setInitialMissingRoles(missingRoles);
        setStoredSignatureNames(res?.data?.stored_signature_names || []);
        const spots = res?.data?.signature_spots;
        if (spots?.ready === false) setNowhereToSignMessage(spots.message || 'This document has nowhere to sign.');
        if (signers.length) {
          setRecipients(signers.map(recipientFromSuggestedSigner));
        } else {
          setRecipients([emptyRecipient()]);
        }
      } catch (err) {
        if (cancelled) return;
        setSuggestedRecipientsError(
          err?.response?.data?.error ||
          'DMS could not resolve the default signing order. You can still enter recipients manually.'
        );
        setInitialMissingRoles([]);
        setRecipients([emptyRecipient()]);
      } finally {
        if (!cancelled) setSuggestedRecipientsLoading(false);
      }
    };

    loadSuggestedRecipients();
    return () => {
      cancelled = true;
    };
  }, [openESignModal, selectedDoc?.id]);


  const handleRecipientChange = (idx, field, value) => {
    setRecipients(recipients =>
      recipients.map((recipient, recipientIndex) => {
        if (recipientIndex !== idx) return recipient;
        if (field === 'signature_method' && value === STORED_SIGNATURE) {
          return { ...recipient, signature_method: value, delivery_method: 'EMAIL' };
        }
        if (field !== 'name') return { ...recipient, [field]: value };

        const selectedSigner = KNOWN_SIGNERS.find((signer) => signer.name === value);
        return {
          ...recipient,
          name: value,
          email: selectedSigner?.email || recipient.email,
        };
      })
    );
  };

  const handleAddRecipient = () => {
    setRecipients([...recipients, emptyRecipient()]);
  };

  // A person typed in on the placement screen (name, email, phone, how to
  // reach them). No role: the boxes say where they sign.
  const isBlankRecipient = ({ name, email }) => !name?.trim() && !email?.trim();
  const handleAddPerson = (person) => {
    // A blank placeholder row (no suggestions came back) gives way to the
    // first real person rather than lingering as a nameless signer.
    setRecipients((current) => [
      ...current.filter((recipient) => !isBlankRecipient(recipient)),
      { ...emptyRecipient(), ...person },
    ]);
  };

  const handleMovePerson = (fromIndex, toIndex) => {
    setRecipients((current) => arrayMove(current, fromIndex, toIndex));
  };

  const belongsTo = (box, recipient, index) => {
    const boxEmail = (box.signer_email || '').trim().toLowerCase();
    if (boxEmail) return boxEmail === (recipient.email || '').trim().toLowerCase();
    return box.signer_position === index + 1;
  };

  const handleRemoveRecipient = idx => {
    // Their boxes go with them, so a removed person can never leave behind
    // boxes that nobody signs (which would block Send).
    const leaving = recipients[idx];
    if (leaving) {
      setPlacedBoxes((current) => current.filter((box) => !belongsTo(box, leaving, idx)));
    }
    setRecipients(recipients => recipients.filter((_, i) => i !== idx));
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = recipients.findIndex((r) => r.id === active.id);
    const newIndex = recipients.findIndex((r) => r.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;

    setRecipients((items) => arrayMove(items, oldIndex, newIndex));
  };

  const isPdfDocument = /\.pdf$/i.test(selectedDoc?.name || '');
  const alignedBoxes = useMemo(
    () => alignBoxesToRecipients(placedBoxes, recipients),
    [placedBoxes, recipients]
  );
  const boxesWithoutSigner = useMemo(
    () => orphanedBoxes(alignedBoxes, recipients),
    [alignedBoxes, recipients]
  );
  const hasSignatureSpots = !nowhereToSignMessage || alignedBoxes.length > 0;
  // With boxes on the page, each person's place to sign is already decided,
  // so the Client / Attorney role is optional for the extra people.
  const usingPlacedBoxes = alignedBoxes.length > 0;
  const showRecipientReadout = usingPlacedBoxes && !editingRecipientDetails;
  const boxCountFor = (recipient, index) =>
    alignedBoxes.filter((box) => belongsTo(box, recipient, index)).length;
  const deliveryLabel = ({ delivery_method: method, phone }) => {
    if (method === 'SMS') return `text to ${phone}`;
    if (method === 'BOTH') return `email + text to ${phone}`;
    return 'email';
  };

  // Arriving from "Place boxes yourself": open placement as soon as the
  // recipients are known, once per opening.
  useEffect(() => {
    if (!openESignModal) {
      autoOpenedPlacementRef.current = false;
      return;
    }
    if (!startWithBoxPlacement || !isPdfDocument || suggestedRecipientsLoading) return;
    if (autoOpenedPlacementRef.current) return;
    autoOpenedPlacementRef.current = true;
    setPlacingBoxes(true);
  }, [openESignModal, startWithBoxPlacement, isPdfDocument, suggestedRecipientsLoading]);

  const handleDisabled = () => {
    if (suggestedRecipientsLoading || effectiveMissingRoles.length || !hasSignatureSpots) return false;
    if (boxesWithoutSigner.length) return false;
    return recipients.every(({ name, email, role, phone, delivery_method }) => {
      if (!name?.trim() || !email?.trim()) return false;
      if (!usingPlacedBoxes && !role?.trim()) return false;
      const method = delivery_method || 'EMAIL';
      if ((method === 'SMS' || method === 'BOTH') && !phone?.trim()) return false;
      return true;
    });
  }

  const handleSend = async () => {
    if (effectiveMissingRoles.length) return;
    const { recipientErrors, hasError } = validateRecipients(recipients, { requireRole: !usingPlacedBoxes });
    setErrors(recipientErrors);
  
    if (hasError) {
      // The readout has no fields to show an error on; open the editor so
      // the problem (bad phone, over-long name…) is visible and fixable.
      if (showRecipientReadout) setEditingRecipientDetails(true);
      return;
    }
  
    setisLoading(true);
    try {
      const signersWithoutId = applyStoredSignatureChoice(
        // The list order is the signing order; say so explicitly so people
        // added without a role keep their place.
        normalizeSignerRolesForSubmit(recipients.map(({ id, ...rest }, index) => ({ ...rest, order: index + 1 }))),
        storedSignatureNames
      );
      const result = await handleSubmitESign({
        signers: signersWithoutId,
        notes,
        selectedDoc,
        ...(alignedBoxes.length ? { fields: alignedBoxes } : {}),
      });
  
      if (result?.success) {
        setOpenESignModal(false);
      }
    } catch (err) {
      console.log("Error in E Sign", err);
    } finally {
      setisLoading(false);
    }
  };
    
  const handleClose = () => {
    setRecipients([emptyRecipient()]);
    setInitialMissingRoles([]);
    setSuggestedRecipientsError('');
    setNotes('');
    setEditingRecipientDetails(false);
    setOpenESignModal(false);
  };
  

  return (
    <>
      {isESignEnabled ? (
        <Modal
          title={
            <ModalHeader>
              <ModalTitle>Send for E-Signature</ModalTitle>
            </ModalHeader>
          }
          open={openESignModal}
          onCancel={handleClose}
          footer={null}
          width={850}
          style={{ top: 147.5, padding: 0 }}
          styles={{
            body: { padding: 0, borderRadius: 12, background: "#fff" },
          }}
        >
          {isLoading && <Loader />}
          <ModalContent>
            <FileSection>
              <FileLabel>File Selected</FileLabel>
              <FileInput
                value={selectedDoc?.name || "No file selected"}
                disabled
              />
            </FileSection>
            <TagInfoBox>
              <Collapse
                ghost
                items={[{
                  key: '1',
                  label: <><InfoCircleOutlined /> Signature tag placement guide</>,
                  children: (
                    <div>
                      <p style={{ margin: '0 0 6px 0' }}>
                        Place these tags in your document where signature fields should appear.
                        Match <TagCode>signerN</TagCode> to the recipient order below (1st recipient = <TagCode>signer1</TagCode>).
                      </p>
                      <TagTable>
                        <thead>
                          <tr><th>Field</th><th>Tag</th></tr>
                        </thead>
                        <tbody>
                          <tr><td>Signature</td><td><TagCode>[sig|req|signer1]</TagCode></td></tr>
                          <tr><td>Initials</td><td><TagCode>[initial|req|signer1]</TagCode></td></tr>
                          <tr><td>Date</td><td><TagCode>[date|req|signer1]</TagCode></td></tr>
                          <tr><td>Text / Name</td><td><TagCode>[text|req|signer1]</TagCode></td></tr>
                          <tr><td>Checkbox</td><td><TagCode>[check|req|signer1]</TagCode></td></tr>
                        </tbody>
                      </TagTable>
                      <p style={{ margin: '8px 0 0 0', fontStyle: 'italic' }}>
                        Use <TagCode>noreq</TagCode> instead of <TagCode>req</TagCode> for optional fields.
                        Tip: set tag font color to white to hide them in the final document.
                      </p>
                    </div>
                  ),
                }]}
              />
            </TagInfoBox>
            {nowhereToSignMessage && !alignedBoxes.length && (
              <RecipientNotice $error data-testid="nowhere-to-sign-warning">
                {nowhereToSignMessage}
                {isPdfDocument && ' Or place signature boxes yourself below.'}
              </RecipientNotice>
            )}
            {isPdfDocument && (
              <RecipientNotice data-testid="placed-boxes-summary">
                {alignedBoxes.length
                  ? `${alignedBoxes.length} signature box${alignedBoxes.length === 1 ? '' : 'es'} placed. They replace any signature spots already in the document. `
                  : 'Need signature spots somewhere specific? '}
                <Button
                  size="small"
                  onClick={() => setPlacingBoxes(true)}
                  data-testid="place-boxes-button"
                  disabled={!recipients.length}
                >
                  {alignedBoxes.length ? 'Edit boxes' : 'Place signature boxes'}
                </Button>
              </RecipientNotice>
            )}
            {boxesWithoutSigner.length > 0 && (
              <RecipientNotice $error data-testid="orphaned-boxes-warning">
                {boxesWithoutSigner.length} signature box{boxesWithoutSigner.length === 1 ? ' belongs' : 'es belong'} to
                someone no longer on the recipient list. Edit the boxes before sending.
              </RecipientNotice>
            )}
            <PlaceSignatureBoxes
              open={placingBoxes}
              document={selectedDoc}
              recipients={recipients}
              initialBoxes={alignedBoxes}
              onCancel={() => setPlacingBoxes(false)}
              onDone={(boxes) => {
                setPlacedBoxes(boxes);
                setPlacingBoxes(false);
                setEditingRecipientDetails(false);
              }}
              onAddRecipient={handleAddPerson}
              onRemoveRecipient={handleRemoveRecipient}
              onMoveRecipient={handleMovePerson}
            />
            <RecipientsSection>
              <RecipientsLabel>Recipients</RecipientsLabel>
              {showRecipientReadout && (
                <>
                  <RecipientNotice data-testid="recipient-readout-notice">
                    These people were set on the placement screen and sign in this order. Check the
                    details, then Send.
                  </RecipientNotice>
                  <ReadoutList data-testid="recipient-readout">
                    {recipients.map((recipient, idx) => {
                      const count = boxCountFor(recipient, idx);
                      return (
                        <ReadoutRow key={recipient.id} data-testid="recipient-readout-row">
                          <ReadoutNumber $color={signerColor(idx + 1)}>{idx + 1}</ReadoutNumber>
                          <ReadoutName>{recipient.name || <em>No name</em>}</ReadoutName>
                          <ReadoutMeta title={recipient.email}>
                            {recipient.email || 'no email'} · {deliveryLabel(recipient)}
                            {recipient.role ? ` · ${ESIGN_ROLE_LABELS[recipient.role] || recipient.role}` : ''}
                          </ReadoutMeta>
                          <ReadoutBoxes $warn={count === 0}>
                            {count === 0 ? 'no boxes' : `${count} box${count === 1 ? '' : 'es'}`}
                          </ReadoutBoxes>
                        </ReadoutRow>
                      );
                    })}
                  </ReadoutList>
                  <ReadoutActions>
                    <a onClick={() => setPlacingBoxes(true)} data-testid="readout-change-people">
                      Change people or boxes
                    </a>
                    <a onClick={() => setEditingRecipientDetails(true)} data-testid="readout-edit-details">
                      Edit details
                    </a>
                  </ReadoutActions>
                </>
              )}
              {!showRecipientReadout && usingPlacedBoxes && (
                <ReadoutActions>
                  <a onClick={() => setEditingRecipientDetails(false)} data-testid="readout-done-editing">
                    Done editing
                  </a>
                </ReadoutActions>
              )}
              {!showRecipientReadout && suggestedRecipientsLoading && (
                <RecipientNotice>
                  Loading the default signing order for this document...
                </RecipientNotice>
              )}
              {!showRecipientReadout && !suggestedRecipientsLoading && !effectiveMissingRoles.length && recipients.some((recipient) => recipient.role) && (
                <RecipientNotice>
                  DMS resolved the signing order from the template: Client first,
                  Referral Attorney when present, then Attorney.
                </RecipientNotice>
              )}
              {!showRecipientReadout && suggestedRecipientsError && (
                <RecipientNotice $error>
                  {suggestedRecipientsError}
                </RecipientNotice>
              )}
              {effectiveMissingRoles.length > 0 && (
                <RecipientNotice $error data-testid="missing-role-warning">
                  DMS cannot send this yet because required signer details are missing:
                  {' '}
                  {effectiveMissingRoles.map(missingRoleLabel).join(', ')}.
                </RecipientNotice>
              )}
              {!showRecipientReadout && (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
                accessibility={{ restoreFocus: false }}
              >
                <SortableContext
                  items={recipients.map((r) => r.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {recipients.map((recipient, idx) => (
                    <SortableRecipientWrapper
                      key={recipient.id}
                      id={recipient.id}
                      idx={idx}
                      isFirst={idx === 0}
                      recipient={recipient}
                      onChange={handleRecipientChange}
                      onRemove={handleRemoveRecipient}
                      canRemove={recipients.length > 1}
                      errors={errors[recipient.id] || {}}
                      roleOptions={roleOptions}
                      storedSignatureAvailable={canUseStoredSignature(recipient, storedSignatureNames)}
                    />
                  ))}
                </SortableContext>
              </DndContext>
              )}
              {!showRecipientReadout && (
                <AddRecipientLink onClick={handleAddRecipient}>
                  <PlusOutlined /> Add Recipient
                </AddRecipientLink>
              )}
            </RecipientsSection>
            <StyledDivider />
            <NoteSection>
              <NoteLabel>Add Note</NoteLabel>
              <NoteInput
                rows={3}
                placeholder="Document Signature Request"
                value={notes}
                onChange={(e) => setNotes(e.target.value.slice(0, 200))}
              />
            </NoteSection>
            <Footer>
              <CancelBtn onClick={handleClose}>Cancel</CancelBtn>
              <SendBtn
                disabled={!handleDisabled()}
                type="primary"
                onClick={handleSend}
              >
                Send
              </SendBtn>
            </Footer>
          </ModalContent>
        </Modal>
      ) : (
        <MessageModal
          open={true}
          onClose={handleClose}
          title={"Warning: E-Signature feature is Disabled"}
          message={
            "E-Signature Request Email notifications are currently disabled. Please contact your admin."
          }
          buttonText={"Close"}
          icon={<WarningIcon />}
          onClick={handleClose}
          variant="warning"
        />
      )}
    </>
  );
};

ESignModal.propTypes = {
  openESignModal: PropTypes.bool.isRequired,
  setOpenESignModal: PropTypes.func.isRequired,
  selectedDoc: PropTypes.object,
  handleSubmitESign: PropTypes.func.isRequired,
  handleSnackbar: PropTypes.func.isRequired,
  startWithBoxPlacement: PropTypes.bool,
};

ESignModal.defaultProps = {
  startWithBoxPlacement: false,
};

export default ESignModal; 